import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { timelineApi } from '@/utils/api';
import type { TimelineEpic, InvestibleStructure } from '@/types';

export interface TimelineState {
  epics: TimelineEpic[];
  investibleStructures: InvestibleStructure[];
  planningPeriods: any[];
  isLoading: boolean;
  isLoadingStructures: boolean;
  isLoadingPeriods: boolean;
  error: string | null;
  filters: {
    investibleStructureId?: string;
    planningPeriodId?: string;
  };
  selectedYear: number;
}

const currentYear = new Date().getFullYear();

const initialState: TimelineState = {
  epics: [],
  investibleStructures: [],
  planningPeriods: [],
  isLoading: false,
  isLoadingStructures: false,
  isLoadingPeriods: false,
  error: null,
  filters: {},
  selectedYear: currentYear,
};

// Async thunks
export const fetchTimelineData = createAsyncThunk(
  'timeline/fetchTimelineData',
  async (params: {
    investibleStructureId?: string;
    planningPeriodId?: string;
  } | undefined, { rejectWithValue }) => {
    try {
      const response = await timelineApi.getTimelineData(params);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch timeline data');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch timeline data');
    }
  }
);

export const fetchInvestibleStructuresForFilter = createAsyncThunk(
  'timeline/fetchInvestibleStructures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await timelineApi.getInvestibleStructures();

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch investible structures');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch investible structures');
    }
  }
);

// Slice
const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch timeline data
    builder
      .addCase(fetchTimelineData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTimelineData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.epics = (action.payload as TimelineEpic[] | undefined) || [];
      })
      .addCase(fetchTimelineData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch investible structures for filter
    builder
      .addCase(fetchInvestibleStructuresForFilter.pending, (state) => {
        state.isLoadingStructures = true;
      })
      .addCase(fetchInvestibleStructuresForFilter.fulfilled, (state, action) => {
        state.isLoadingStructures = false;
        state.investibleStructures = (action.payload as InvestibleStructure[] | undefined) || [];
      })
      .addCase(fetchInvestibleStructuresForFilter.rejected, (state, action) => {
        state.isLoadingStructures = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, setSelectedYear, clearError } = timelineSlice.actions;
export default timelineSlice.reducer;
